import type { FC, ReactNode } from 'react';
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import type { TwoDimensionalData } from 'src/components/followUp/common/types';

import type { BarTableCellData } from './BarTableCellWithHiddenBar';
import BarTableCellWithHiddenBar from './BarTableCellWithHiddenBar';

import styles from './index.styles';

interface BarTableProps {
  firstCell?: ReactNode;
  data: TwoDimensionalData<BarTableCellData>;
  aggregateLabels: Array<string>;
  firstColumnWidth?: string;
}

const BarTable: FC<BarTableProps> = ({
  firstCell,
  data,
  aggregateLabels,
  firstColumnWidth,
}) => (
  <Box sx={styles.barTableWidget}>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.firstColumn} width={firstColumnWidth}>
              {firstCell}
            </TableCell>

            {aggregateLabels.map((aggregateLabel) => (
              <TableCell key={aggregateLabel} align="left">
                <Tooltip
                  arrow
                  enterTouchDelay={1}
                  leaveTouchDelay={3000}
                  placement="bottom"
                  title={aggregateLabel}
                >
                  <Typography sx={styles.rotateText} component="span">
                    {aggregateLabel}
                  </Typography>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map(({ label, stacks }) => (
            <TableRow key={label}>
              <TableCell
                align="left"
                sx={styles.firstColumn}
                width={firstColumnWidth}
              >
                <Tooltip
                  arrow
                  enterTouchDelay={1}
                  leaveTouchDelay={3000}
                  placement="top"
                  title={label}
                >
                  <Typography sx={styles.firstColumnText} component="span">
                    {label}
                  </Typography>
                </Tooltip>
              </TableCell>

              {stacks.map(
                ({
                  stackLabel,
                  value,
                  comparison,
                  color,
                  centeredScale,
                  hideBar,
                  percentage,
                }) =>
                  hideBar ? (
                    <BarTableCellWithHiddenBar
                      key={stackLabel}
                      value={value}
                      comparison={comparison}
                      color={color}
                    />
                  ) : (
                    <TableCell key={stackLabel} align="left">
                      <Box
                        sx={(theme) =>
                          styles.bar(
                            theme,
                            {
                              comparisonCount: percentage ?? value,
                              comparisonDiff: comparison,
                            },
                            color,
                            centeredScale,
                          )
                        }
                      />

                      <Typography
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        {percentage ? `${percentage}% / ` : ''}
                        {value}
                        {comparison && (
                          <NumberDifference
                            comparisonDiff={comparison}
                            showArrow
                          />
                        )}
                      </Typography>
                    </TableCell>
                  ),
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default BarTable;
